import { urlUtil } from '@cmg/common';
import isNil from 'lodash/isNil';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { parseFirmKey, serializeFirmKey } from '../../../common/util/rolodex/firm-key-type.util';

export { parseFirmKey, serializeFirmKey };

const useGetCmgEntityKeyQueryParam = () => {
  const { search } = useLocation();
  return (urlUtil.queryParse(search)?.cmgEntityKey as string) || undefined;
};

const useUpdateCmgEntityKey = () => {
  const { search, pathname } = useLocation();
  const navigate = useNavigate();

  return (newCmgEntityKey?: string) => {
    const { cmgEntityKey, ...restCurrentQueryParams } = urlUtil.queryParse(search);

    // If use uses investor dropdown to navigate to a new investor, we want to
    // delete indicationId query param from the URL.
    if (restCurrentQueryParams.indicationId) {
      delete restCurrentQueryParams.indicationId;
    }
    // If the new CMG Entity Key value is truthy, add or update it in the
    // query string - and persist all other query params.
    const updatedSearch = {
      ...restCurrentQueryParams,
      ...(newCmgEntityKey ? { cmgEntityKey: newCmgEntityKey } : {}),
    };

    navigate({
      pathname,
      search: urlUtil.queryStringify(updatedSearch),
    });
  };
};

type CmgEntityKeyContextValue = {
  cmgEntityKey?: string;
  updateCmgEntityKey: (cmgEntityKey?: string) => void;
};

const CmgEntityKeyContext = React.createContext<CmgEntityKeyContextValue>({
  updateCmgEntityKey: useUpdateCmgEntityKey,
});

/**
 * Hook that exposes a reference to the CMG Entity Key Query param,
 * as well as a function to modify that query param.
 */
export const useCmgEntityKeyContext = () => {
  const context = React.useContext(CmgEntityKeyContext);

  if (isNil(context)) {
    throw new Error('No Provider found for CmgEntityKeyContext');
  }

  return context;
};

/**
 * Central interface for dealing with CMG Entity Keys in Routes.
 *
 * React context provider that parses the CMG Entity Key
 * Query Parameter from the current URL and exposes it to context
 * consumers.  Also provides a utility for updating the CMG Entity Key
 * Query Param.
 */
export const CmgEntityKeyProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const cmgEntityKey = useGetCmgEntityKeyQueryParam();
  const updateCmgEntityKey = useUpdateCmgEntityKey();

  return (
    <CmgEntityKeyContext.Provider value={{ cmgEntityKey, updateCmgEntityKey }}>
      {children}
    </CmgEntityKeyContext.Provider>
  );
};
